if (!customElements.get('jersey-personalization-guard')) {
  // A cart re-render can mount more than one guard instance for the same line
  // (e.g. the jersey's own removal re-renders the drawer before this line's
  // fix-up request resolves), so track in-flight keys to avoid a duplicate
  // request landing on a line that a sibling request already cleared. The
  // value is the in-flight promise itself, so the checkout button below can
  // hold the customer's cart in place until every fix-up has landed.
  const pendingLineKeys = new Map();

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    return sections;
  }

  async function publishCart(parsedState) {
    // A `status` field means the request errored (most likely because a sibling
    // guard instance already resolved this line) — nothing left to refresh.
    if (parsedState.status) return;

    const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
    cartJson['sections'] = parsedState['sections'];
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'jersey-personalization-guard', cart: cartJson });
  }

  function removeLine(lineKey) {
    const body = JSON.stringify({ id: lineKey, quantity: 0, sections: bundledSections() });

    return fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => publishCart(parsedState))
      .catch((error) => {
        console.error(error);
      });
  }

  function stripPersonalization(lineKey, variantId, quantity, properties) {
    const nameKey = properties['_jp_name_property'];
    delete properties['_jp_fee_variant'];
    delete properties['_jp_name_property'];
    if (nameKey) delete properties[nameKey];

    // /cart/change.js ignores an empty `properties` payload instead of clearing
    // existing properties, so the line has to be removed and re-added instead.
    return fetch(theme.routes.cart_change_url, {
      ...theme.utils.fetchConfig(),
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
      .then((response) => response.json())
      .then((removedState) => {
        if (removedState.status) return removedState;

        const items = [{ id: variantId, quantity: parseInt(quantity, 10) }];
        if (Object.keys(properties).length > 0) items[0].properties = properties;

        return fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items, sections: bundledSections() })
        }).then((response) => response.json());
      })
      .then((parsedState) => publishCart(parsedState))
      .catch((error) => {
        console.error(error);
      });
  }

  function trackPending(lineKey, promise) {
    const tracked = promise.finally(() => pendingLineKeys.delete(lineKey));
    pendingLineKeys.set(lineKey, tracked);
    return tracked;
  }

  // Belt-and-suspenders check that runs right before checkout, independent of
  // whatever guard elements may or may not have mounted from a section
  // render. Re-reads the cart straight from the server so a stale drawer
  // (never re-rendered since the fee/jersey mismatch appeared) can't slip a
  // free personalization or an orphaned fee line through to checkout.
  async function verifyCartBeforeCheckout() {
    const cart = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
    const quantityForVariant = (variantId) =>
      cart.items
        .filter((cartItem) => String(cartItem.variant_id) === String(variantId))
        .reduce((sum, cartItem) => sum + cartItem.quantity, 0);

    cart.items.forEach((item) => {
      const lineKey = item.key;
      if (pendingLineKeys.has(lineKey)) return;

      const feeVariantId = item.properties && item.properties['_jp_fee_variant'];
      const linkedVariantId = item.properties && item.properties['_jp_linked_variant'];
      const nameKey = item.properties && item.properties['_jp_name_property'];
      const nameValue = nameKey ? item.properties[nameKey] : undefined;
      const hasBlankName = feeVariantId && (!nameKey || !String(nameValue ?? '').trim());

      if (feeVariantId && (quantityForVariant(feeVariantId) < item.quantity || hasBlankName)) {
        trackPending(lineKey, stripPersonalization(lineKey, item.variant_id, item.quantity, { ...item.properties }));
      } else if (linkedVariantId && quantityForVariant(linkedVariantId) < item.quantity) {
        trackPending(lineKey, removeLine(lineKey));
      }
    });

    if (pendingLineKeys.size > 0) await Promise.all(Array.from(pendingLineKeys.values()));
  }

  // The cart's "Checkout" button is a plain form submit straight to Shopify's
  // hosted checkout, which reads whatever the cart currently is server-side —
  // it has no idea a fix-up fetch is (or should be) in flight. Hold it here
  // until the cart is verified clean, then let the original click through.
  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    if (submitter.hasAttribute('data-jp-verified')) return;

    event.preventDefault();
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');

    verifyCartBeforeCheckout().finally(() => {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
      submitter.setAttribute('data-jp-verified', '');
      event.target.requestSubmit(submitter);
    });
  }, true);

  customElements.define(
    'jersey-personalization-guard',
    class JerseyPersonalizationGuard extends HTMLElement {
      connectedCallback() {
        const lineKey = this.getAttribute('data-line-key');
        if (!lineKey || pendingLineKeys.has(lineKey)) return;

        if (this.getAttribute('data-action') === 'remove') {
          trackPending(lineKey, removeLine(lineKey));
          return;
        }

        const variantId = this.getAttribute('data-variant-id');
        const quantity = this.getAttribute('data-quantity');
        const propertiesScript = this.querySelector('script[data-properties]');
        if (!variantId || !quantity || !propertiesScript) return;

        let properties;
        try {
          // Liquid's `json` filter serializes `item.properties` as an array of
          // [key, value] pairs, not a plain object, so it needs converting first.
          properties = Object.fromEntries(JSON.parse(propertiesScript.textContent));
        } catch (error) {
          return;
        }

        trackPending(lineKey, stripPersonalization(lineKey, variantId, quantity, properties));
      }
    }
  );
}
